import { useState } from "react";

import { useDeleteImgaKeyMutation, useImgaStatusQuery, useSaveImgaKeyMutation, useTestImgaMutation } from "@/api/admin";
import { ApiError } from "@/api/client";
import { Button } from "@/components/ui";
import { t } from "@/i18n";
import { fmtDateTime } from "@/lib/utils";

/**
 * Gorev C (esitlik) — /admin "İmga AI" karti, eski legacy/admin/settings.html
 * "İmga AI Servisi" karti + js/admin-settings.js ile ayni islev (durum + test/
 * kaydet/sil). Anahtar KAYDEDILINCE input temizlenir, konsola/loga YAZILMAZ.
 */
export function ImgaCard() {
  const statusQuery = useImgaStatusQuery();
  const saveMutation = useSaveImgaKeyMutation();
  const deleteMutation = useDeleteImgaKeyMutation();
  const testMutation = useTestImgaMutation();

  const [apiKeyInput, setApiKeyInput] = useState("");
  const [message, setMessage] = useState<{ kind: "success" | "error"; text: string } | null>(null);

  function errorMessage(error: unknown): string {
    return error instanceof ApiError && error.message ? error.message : t("admin.imga.error");
  }

  async function handleSave() {
    setMessage(null);
    const key = apiKeyInput.trim();
    if (!key) {
      setMessage({ kind: "error", text: t("admin.imga.key_required") });
      return;
    }
    try {
      await saveMutation.mutateAsync(key);
      // Sensitive girdi kaydedilince DOM'dan temizlenir (bkz. gorev talimati).
      setApiKeyInput("");
      setMessage({ kind: "success", text: t("admin.imga.save_success") });
    } catch (err) {
      setMessage({ kind: "error", text: errorMessage(err) });
    }
  }

  async function handleDelete() {
    if (!window.confirm(t("admin.imga.delete_confirm"))) return;
    setMessage(null);
    try {
      await deleteMutation.mutateAsync();
      setMessage({ kind: "success", text: t("admin.imga.delete_success") });
    } catch (err) {
      setMessage({ kind: "error", text: errorMessage(err) });
    }
  }

  async function handleTest() {
    setMessage(null);
    try {
      const result = await testMutation.mutateAsync(apiKeyInput.trim() || undefined);
      if (result.ok) {
        const note = result.mock ? ` (${t("admin.imga.status_mock_note")})` : "";
        setMessage({ kind: "success", text: `${t("admin.imga.test_success")}${note}` });
      } else {
        setMessage({ kind: "error", text: result.error || t("admin.imga.error") });
      }
    } catch (err) {
      setMessage({ kind: "error", text: errorMessage(err) });
    }
  }

  const status = statusQuery.data;
  const isBusy = saveMutation.isPending || deleteMutation.isPending || testMutation.isPending;

  return (
    <div className="card space-y-3">
      <h2 className="text-sm font-bold uppercase tracking-wide text-muted">{t("admin.imga.title")}</h2>
      <p className="text-xs text-muted">{t("admin.imga.description")}</p>

      {statusQuery.isLoading && <p className="text-sm text-muted">{t("common.loading")}</p>}
      {statusQuery.isError && (
        <p role="alert" className="text-sm text-red">
          {t("common.error")}
        </p>
      )}

      {status && (
        <div className="space-y-1 text-sm">
          <p className={`font-semibold ${status.api_key_set ? "text-green" : "text-amber"}`}>
            {status.api_key_set ? t("admin.imga.status_connected") : t("admin.imga.status_not_connected")}
          </p>
          {status.mock && <p className="text-xs text-amber">{t("admin.imga.status_mock_note")}</p>}
          {status.updated_at && (
            <p className="text-xs text-muted">{t("admin.imga.updated_at", { time: fmtDateTime(status.updated_at) })}</p>
          )}
          <p className="text-xs text-muted">
            {t("admin.imga.api_url")}: <span className="font-mono">{status.api_url}</span>
          </p>
          <p className="text-xs text-muted">
            {t("admin.imga.tenant_id")}: <span className="font-mono">{status.tenant_id}</span>
          </p>
        </div>
      )}

      <label className="block">
        <span className="label">{t("admin.imga.api_key_label")}</span>
        <input
          type="password"
          autoComplete="off"
          placeholder={t("admin.imga.api_key_placeholder")}
          value={apiKeyInput}
          onChange={(e) => setApiKeyInput(e.target.value)}
          className="input"
        />
      </label>

      {message && (
        <p role={message.kind === "error" ? "alert" : "status"} className={`text-sm ${message.kind === "error" ? "text-red" : "text-green"}`}>
          {message.text}
        </p>
      )}

      <div className="flex flex-wrap gap-2">
        <Button type="button" variant="outline" disabled={isBusy} onClick={() => void handleTest()}>
          {testMutation.isPending ? t("admin.imga.testing") : t("admin.imga.test")}
        </Button>
        <Button type="button" variant="primary" disabled={isBusy} onClick={() => void handleSave()}>
          {saveMutation.isPending ? t("admin.imga.saving") : t("admin.imga.save")}
        </Button>
        <Button type="button" variant="danger" disabled={isBusy} onClick={() => void handleDelete()}>
          {deleteMutation.isPending ? t("admin.imga.deleting") : t("admin.imga.delete")}
        </Button>
      </div>
    </div>
  );
}
